import { isInjectable, isSingleton } from "../di/decorators.js";
import { isLazyModuleImport, resolveLazyModuleImport } from "./types.js";
import { ExtendHelper } from "./extend-helper.js";
import { Controller } from "./controller.js";
class ModuleRefImpl {
    constructor(moduleClass, metadata, container){
        this.importedModuleRefs = new Map();
        this.lazyImportCache = new Map();
        this.instanceCache = new Map();
        this.moduleClass = moduleClass;
        this.metadata = metadata;
        this.container = container;
    }
    applyExtensionsIfAny(originalClass) {
        const extendHelper = ExtendHelper.getInstance();
        return extendHelper.applyExtensions(originalClass);
    }
    getContainer() {
        return this.container;
    }
    _setModuleInstancesRegistry(registry) {
        this.moduleInstancesRegistry = registry;
    }
    _setImportedModuleRef(moduleClass, moduleRef) {
        this.importedModuleRefs.set(moduleClass, moduleRef);
    }
    _setModuleLoader(loader) {
        this.moduleLoader = loader;
    }
    async loadModule(moduleImport, options) {
        if (!this.moduleLoader) throw new Error('Module loader not initialized. Application must be bootstrapped first.');
        if (options?.singleton && isLazyModuleImport(moduleImport)) {
            const cacheKey = moduleImport.toString();
            const cached = this.lazyImportCache.get(cacheKey);
            if (void 0 !== cached) return cached;
            const resultPromise = this.moduleLoader(moduleImport, options);
            this.lazyImportCache.set(cacheKey, resultPromise);
            return resultPromise;
        }
        return this.moduleLoader(moduleImport, options);
    }
    get(token) {
        if (this.instanceCache.has(token)) return this.instanceCache.get(token);
        if (this.moduleInstancesRegistry && this.moduleInstancesRegistry.has(token)) {
            const instance = this.moduleInstancesRegistry.get(token);
            this.instanceCache.set(token, instance);
            return instance;
        }
        const instance = this.container.get(token);
        this.instanceCache.set(token, instance);
        return instance;
    }
    async resolve(token, options) {
        if ('function' == typeof token && !this.container.has(token) && (isInjectable(token) || isSingleton(token))) {
            const extendedClass = this.applyExtensionsIfAny(token);
            const shouldBeSingleton = isSingleton(token);
            this.container.register({
                type: 'type',
                token: token,
                useClass: extendedClass,
                singleton: shouldBeSingleton
            });
        }
        const instance = await this.container.resolve(token, options);
        if (instance instanceof Controller) instance._setModuleRef(this);
        return instance;
    }
    createStandaloneClassInstance(cls, params) {
        const extendedClass = this.applyExtensionsIfAny(cls);
        return new extendedClass(...params);
    }
    getProviders() {
        return this.metadata.providers || [];
    }
    getControllers() {
        return this.metadata.controllers || [];
    }
    getImports() {
        return this.metadata.imports || [];
    }
    async getResolvedImports() {
        const imports = this.metadata.imports || [];
        const resolvedImports = [];
        for (const moduleImport of imports)if (isLazyModuleImport(moduleImport)) resolvedImports.push(await resolveLazyModuleImport(moduleImport));
        else resolvedImports.push(moduleImport);
        return resolvedImports;
    }
    getExports() {
        return this.metadata.exports || [];
    }
    getMetadata() {
        return this.metadata;
    }
    getModuleClass() {
        return this.moduleClass;
    }
    hasProvider(token) {
        return this.getProviders().includes(token);
    }
    hasController(token) {
        return this.getControllers().includes(token);
    }
    hasImport(moduleClass) {
        return this.getImports().includes(moduleClass);
    }
    hasExport(token) {
        return this.getExports().includes(token);
    }
    getProviderInstances() {
        return this.getProviders().map((provider)=>this.get(provider));
    }
    getControllerInstances() {
        return this.getControllers().map((controller)=>this.get(controller));
    }
    findProvider(predicate) {
        const provider = this.getProviders().find(predicate);
        return provider ? this.container.get(provider) : void 0;
    }
    findController(predicate) {
        const controller = this.getControllers().find(predicate);
        return controller ? this.container.get(controller) : void 0;
    }
    select(token) {
        if (!this.hasProvider(token) && !this.hasController(token)) throw new Error(`Token ${token.name || token} is not available in this module context`);
        return this.get(token);
    }
    getImportedModules() {
        return Array.from(this.importedModuleRefs.values());
    }
    getImportedModule(moduleClass) {
        return this.importedModuleRefs.get(moduleClass);
    }
    getImportedProviders() {
        const allProviders = [];
        for (const moduleRef of this.importedModuleRefs.values())allProviders.push(...moduleRef.getExports().filter((exp)=>moduleRef.getProviders().includes(exp)));
        return allProviders;
    }
    getImportedControllers() {
        const allControllers = [];
        for (const moduleRef of this.importedModuleRefs.values())allControllers.push(...moduleRef.getExports().filter((exp)=>moduleRef.getControllers().includes(exp)));
        return allControllers;
    }
    getImportedExports() {
        const allExports = [];
        for (const moduleRef of this.importedModuleRefs.values())allExports.push(...moduleRef.getExports());
        return allExports;
    }
    getImportedProviderInstances() {
        const instances = [];
        for (const moduleRef of this.importedModuleRefs.values()){
            const exportedProviders = moduleRef.getExports().filter((exp)=>moduleRef.getProviders().includes(exp));
            for (const provider of exportedProviders)try {
                instances.push(moduleRef.get(provider));
            } catch (error) {}
        }
        return instances;
    }
    getImportedControllerInstances() {
        const instances = [];
        for (const moduleRef of this.importedModuleRefs.values()){
            const exportedControllers = moduleRef.getExports().filter((exp)=>moduleRef.getControllers().includes(exp));
            for (const controller of exportedControllers)try {
                instances.push(moduleRef.get(controller));
            } catch (error) {}
        }
        return instances;
    }
    findInImports(token) {
        for (const moduleRef of this.importedModuleRefs.values())if (moduleRef.getExports().includes(token)) try {
            return moduleRef.get(token);
        } catch (error) {
            continue;
        }
    }
    selectFromImports(token) {
        const instance = this.findInImports(token);
        if (!instance) throw new Error(`Token ${token.name || token} is not available in imported modules`);
        return instance;
    }
    hasInImports(token) {
        for (const moduleRef of this.importedModuleRefs.values())if (moduleRef.getExports().includes(token)) return true;
        return false;
    }
    getAllAvailableTokens() {
        const tokens = [
            ...this.getProviders(),
            ...this.getControllers()
        ];
        tokens.push(...this.getImportedExports());
        return Array.from(new Set(tokens));
    }
    findImportedModule(predicate) {
        const moduleRef = Array.from(this.importedModuleRefs.values()).find(predicate);
        return moduleRef ? this.container.get(moduleRef.getModuleClass()) : void 0;
    }
    getImportedModuleInstances() {
        const instances = [];
        for (const importedModuleRef of this.importedModuleRefs.values())try {
            const moduleClass = importedModuleRef.getModuleClass();
            const moduleInstance = importedModuleRef.get(moduleClass);
            if (moduleInstance) instances.push(moduleInstance);
        } catch (error) {}
        return instances;
    }
    callOnImportedModules(methodName, ...args) {
        const instances = this.getImportedModuleInstances();
        for (const instance of instances)if (instance && 'function' == typeof instance[methodName]) try {
            instance[methodName](...args);
        } catch (error) {
            console.warn(`Failed to call ${methodName} on module:`, error);
        }
    }
    findImportedModuleInstance(predicate) {
        const instances = this.getImportedModuleInstances();
        return instances.find(predicate);
    }
    getImportedModuleByName(moduleName) {
        return this.findImportedModuleInstance((instance)=>instance?.constructor?.name === moduleName);
    }
}
export { ModuleRefImpl };
